import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ShieldCheck, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BACKEND } from "@/lib/api";
import { api, errMsg } from "@/lib/api";

function QuickVouch() {
  const qc = useQueryClient();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/feedback", { rating, text });
      toast.success("Vouch posted. Thanks!");
      setText(""); setRating(5);
      qc.invalidateQueries({ queryKey: ["vouches"] });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="osrs-panel mb-8 p-5" data-testid="quick-vouch-form">
      <div className="overline-label mb-3 !text-[#35d07f]">Bought from us? Drop a vouch — no order number needed</div>
      <div className="flex gap-1 mb-3" role="radiogroup" aria-label="Star rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} onClick={() => setRating(n)} aria-label={`${n} stars`} className="transition-transform hover:scale-110" data-testid={`quick-vouch-star-${n}`}>
            <Star size={28} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-[#ffc72c]" : "text-[#5a4a2a]"} />
          </button>
        ))}
      </div>
      <input type="text" name="website" autoComplete="off" tabIndex={-1} className="hidden" aria-hidden="true" onChange={() => {}} data-testid="quick-vouch-honeypot" />
      <textarea required minLength={3} maxLength={400} rows={2} className="field" placeholder="How was your trade?" value={text} onChange={(e) => setText(e.target.value)} data-testid="quick-vouch-text-input" />
      <button className="btn-bomb !py-2 mt-3" disabled={busy || text.trim().length < 3} data-testid="quick-vouch-submit-button">{busy ? "Posting..." : "Post vouch"}</button>
    </form>
  );
}

export default function VouchWall() {
  const { data } = useQuery({ queryKey: ["vouches"], queryFn: () => api.get("/vouches").then((r) => r.data), staleTime: 60000 });
  const items = Array.isArray(data?.items) ? data.items : [];
  return (
    <section className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28" data-testid="vouch-wall-section">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline-label mb-3">Straight from our Discord</div>
          <h2 className="font-display text-5xl leading-[0.9] text-[#f4ead3] md:text-7xl">Vouch wall</h2>
        </div>
        <div className="inline-flex items-center gap-2 border border-[#6b5737] bg-[#21180d] px-4 py-2 font-rs text-sm text-[#ffe83a] rs-shadow" data-testid="vouch-total">
          <ShieldCheck size={18} weight="fill" className="text-[#35d07f]" /> {data?.total || 0} verified vouches
        </div>
      </div>
      <QuickVouch />
      {items.length === 0 && <div className="text-sm text-[#777]" data-testid="vouch-wall-empty">No vouches yet — be the first to drop one above.</div>}
      <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
        {items.slice(0, 12).map((v, i) => (
          <motion.figure key={v.message_id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }}
            className="osrs-panel mb-4 break-inside-avoid p-5" data-testid={`vouch-card-${i}`}>
            {v.rating > 0 && (
              <div className="mb-3 flex items-center justify-between" data-testid={`vouch-stars-${i}`}>
                <span className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={18} weight="fill" className={n <= v.rating ? "text-[#ffc72c]" : "text-[#4a3d25]"} />)}</span>
                <span className="text-[10px] uppercase tracking-[0.14em] text-[#8c7e62]">{new Date(v.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
            )}
            <blockquote className="text-[15px] leading-relaxed text-[#e8dcc0]" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>&ldquo;{v.text}&rdquo;</blockquote>
            {v.images?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {v.images.map((src) => <a key={src} href={`${BACKEND}${src}`} target="_blank" rel="noreferrer"><img src={`${BACKEND}${src}`} alt="Vouch screenshot" loading="lazy" className="max-h-40 border border-black object-cover" /></a>)}
              </div>
            )}
            <figcaption className="mt-4 flex items-center gap-3 border-t border-black/40 pt-3">
              <span className="grid h-8 w-8 place-items-center border border-black bg-[#2a2013] text-[#ffc72c]"><ShieldCheck size={18} weight="fill" /></span>
              <span className="font-rs text-sm text-[#ffe83a] rs-shadow">Anonymous buyer</span>
              {!(v.rating > 0) && <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-[#8c7e62]">{new Date(v.created_at).toLocaleDateString()}</span>}
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}
